import {
  motion,
  useInView,
  useMotionValue,
  useTransform,
  animate,
} from "framer-motion";
import { useRef, useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Link, useLocalizedNavigate } from "@/components/LocalizedLink";
import { useQuery } from "@tanstack/react-query";
import emailjs from "@emailjs/browser";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  PremiumDivider,
  PremiumFieldLabel,
  PremiumStepPill,
  PremiumSurface,
} from "@/components/ui/spotlight-card";
import { useToast } from "@/components/ui/use-toast";
import { fetchContactContent, getLocalizedText } from "@/lib/contentService";
import { trackContactFormLead, trackPhoneClick } from "@/lib/analyticsEvents";
import {
  BUSINESS_LOCATION,
  formatBusinessAddress,
} from "@/lib/businessLocation";
import {
  CONTACT_FORM_FIRST_FIELD_ID,
  CONTACT_FORM_ID,
  CONTACT_PHONE_DISPLAY,
  CONTACT_PHONE_E164,
} from "@/lib/contactDetails";
import { getVisibleEntityLinks } from "@/lib/entityProfiles";
import { STUDIO_PUBLIC_STATS } from "@/lib/studioStats";
import {
  buildContactEmailJsParams,
  buildToolEstimateMessagePrefill,
  formatLeadAttributionForEmail,
  formatToolLeadSummary,
  generateLeadId,
  HEAR_ABOUT_OPTION_IDS,
  mergeLeadAttributionFromQuery,
  resolveContactProjectTypePrefill,
  type HearAboutOptionId,
  type LeadAttribution,
} from "@/lib/leadAttribution";

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
        },
      ) => string;
      reset: (widgetId?: string) => void;
    };
  }
}

const MIN_HUMAN_SUBMIT_MS = 3000;

const AnimatedCounter = ({
  target,
  suffix = "",
}: {
  target: number;
  suffix?: string;
}) => {
  const count = useMotionValue(0);
  const rounded = useTransform(count, (v) => Math.round(v));
  const [display, setDisplay] = useState<number | null>(null);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    if (!isInView || prefersReducedMotion) return;
    count.set(0);
    const controls = animate(count, target, {
      duration: 2,
      ease: [0.22, 1, 0.36, 1],
    });
    const unsub = rounded.on("change", (v) => setDisplay(v));
    return () => {
      controls.stop();
      unsub();
    };
  }, [isInView, target, count, rounded, prefersReducedMotion]);

  const shown = prefersReducedMotion ? target : (display ?? target);

  return (
    <span
      ref={ref}
      className="font-display text-4xl lg:text-5xl font-bold text-foreground tabular-nums"
      aria-label={`${target}${suffix}`}
    >
      {shown}
      {suffix}
    </span>
  );
};

type ContactSectionProps = {
  /** Only the standalone contact page owns the page H1; embedded on the homepage it must stay an H2. */
  headingLevel?: "h1" | "h2";
};

const ContactSection = ({ headingLevel = "h2" }: ContactSectionProps) => {
  const Heading = headingLevel === "h1" ? motion.h1 : motion.h2;
  const { t, lang } = useLanguage();
  const { toast } = useToast();
  const navigate = useLocalizedNavigate();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
    projectType: "",
    hearAbout: "" as HearAboutOptionId | "",
  });
  const [honeypot, setHoneypot] = useState("");
  const formMountedAtRef = useRef(Date.now());
  const [focused, setFocused] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [searchParams] = useSearchParams();
  const [leadAttribution, setLeadAttribution] =
    useState<LeadAttribution | null>(null);
  const { data: contact } = useQuery({
    queryKey: ["singleton", "contact"],
    queryFn: fetchContactContent,
  });

  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as
    | string
    | undefined;
  const turnstileVerifyUrl = import.meta.env.VITE_TURNSTILE_VERIFY_API_URL as
    | string
    | undefined;
  const turnstileContainerRef = useRef<HTMLDivElement>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!turnstileSiteKey || !turnstileContainerRef.current) return undefined;

    let cancelled = false;
    const render = () => {
      if (cancelled || !turnstileContainerRef.current || !window.turnstile)
        return;
      turnstileWidgetIdRef.current = window.turnstile.render(
        turnstileContainerRef.current,
        {
          sitekey: turnstileSiteKey,
          callback: (token) => setTurnstileToken(token),
          "expired-callback": () => setTurnstileToken(null),
          "error-callback": () => setTurnstileToken(null),
        },
      );
    };

    // Loaded lazily here (not in index.html <head>) — Turnstile is only ever
    // needed on this form, so every other page skipped this third-party script
    // and its parse/exec cost entirely.
    const scriptSrc = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    if (window.turnstile) {
      render();
    } else {
      let script = document.querySelector<HTMLScriptElement>(
        `script[src="${scriptSrc}"]`,
      );
      if (!script) {
        script = document.createElement("script");
        script.src = scriptSrc;
        script.async = true;
        document.head.appendChild(script);
      }
      script.addEventListener("load", render);
    }

    return () => {
      cancelled = true;
    };
  }, [turnstileSiteKey]);

  const contactTitle = getLocalizedText(
    contact?.title,
    lang,
    t("contact.title"),
  );
  const contactDescription = getLocalizedText(
    contact?.description,
    lang,
    t("contact.desc"),
  );
  const contactEmail = contact?.email || t("contact.info.email");
  const locationLabel = getLocalizedText(
    contact?.address,
    lang,
    formatBusinessAddress(lang),
  );
  const mapEmbedUrl = contact?.mapUrl?.trim() || BUSINESS_LOCATION.mapEmbedUrl;
  const socialLinks = contact?.socialLinks?.length
    ? contact.socialLinks
    : getVisibleEntityLinks(lang).map((item) => ({
        platform: item.label,
        url: item.url,
      }));
  const formEnabled = contact?.formEnabled ?? true;

  useEffect(() => {
    setLeadAttribution(mergeLeadAttributionFromQuery(searchParams));
    const projectTypeRaw = searchParams.get('project_type');
    if (!projectTypeRaw?.trim()) return;
    setFormData((prev) => ({
      ...prev,
      projectType: resolveContactProjectTypePrefill(projectTypeRaw, {
        ai: t('contact.projectType.ai'),
        saas: t('contact.projectType.saas'),
        mobile: t('contact.projectType.mobile'),
        custom: t('contact.projectType.custom'),
        other: t('contact.projectType.other'),
      }),
    }));
  }, [searchParams, t]);

  useEffect(() => {
    const prefill = buildToolEstimateMessagePrefill(
      leadAttribution?.toolEstimateSummary,
      lang,
    );
    if (!prefill) return;
    setFormData((prev) => {
      if (prev.message.trim()) return prev;
      return { ...prev, message: prefill };
    });
  }, [leadAttribution?.toolEstimateSummary, lang]);

  const hearAboutLabel = useMemo(() => {
    if (!formData.hearAbout) return "";
    return t(`contact.hearAbout.${formData.hearAbout}`);
  }, [formData.hearAbout, t]);

  const attributionFooter = useMemo(
    () => formatLeadAttributionForEmail(leadAttribution, lang, hearAboutLabel),
    [leadAttribution, lang, hearAboutLabel],
  );

  const toolLeadSummary = useMemo(
    () => formatToolLeadSummary(leadAttribution, formData.projectType, lang),
    [leadAttribution, formData.projectType, lang],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formEnabled) {
      return;
    }

    // Honeypot: invisible to real users, bots that blindly fill every input trip it.
    if (honeypot.trim()) {
      return;
    }

    // Bots typically submit far faster than a human can read + fill the form.
    if (Date.now() - formMountedAtRef.current < MIN_HUMAN_SUBMIT_MS) {
      return;
    }

    const trimmed = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      message: formData.message.trim(),
      projectType: formData.projectType.trim(),
    };

    if (!trimmed.name || !trimmed.email || !trimmed.message) {
      toast({
        title: lang === "tr" ? "Eksik alanlar var" : "Missing fields",
        description:
          lang === "tr"
            ? "Lütfen ad, e-posta ve mesaj alanlarını doldurun."
            : "Please fill in name, email, and message fields.",
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed.email)) {
      toast({
        title: lang === "tr" ? "Geçersiz e-posta" : "Invalid email",
        description:
          lang === "tr"
            ? "Lütfen geçerli bir e-posta adresi girin."
            : "Please enter a valid email address.",
      });
      return;
    }

    if (turnstileSiteKey && !turnstileToken) {
      toast({
        title: lang === "tr" ? "Doğrulama gerekli" : "Verification required",
        description:
          lang === "tr"
            ? "Lütfen güvenlik doğrulamasını tamamlayın."
            : "Please complete the security verification.",
      });
      return;
    }

    // A second submit before React re-renders the disabled button must not send or track twice.
    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);

    try {
      if (turnstileSiteKey && turnstileToken && turnstileVerifyUrl) {
        try {
          const verifyResponse = await fetch(turnstileVerifyUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token: turnstileToken }),
          });
          const verifyResult = (await verifyResponse.json()) as {
            success?: boolean;
          };
          if (!verifyResult.success) {
            toast({
              title:
                lang === "tr" ? "Doğrulama başarısız" : "Verification failed",
              description:
                lang === "tr"
                  ? "Güvenlik doğrulaması başarısız oldu. Lütfen tekrar deneyin."
                  : "Security verification failed. Please try again.",
            });
            if (turnstileWidgetIdRef.current)
              window.turnstile?.reset(turnstileWidgetIdRef.current);
            setTurnstileToken(null);
            setIsSubmitting(false);
            return;
          }
        } catch {
          // Verification endpoint unreachable — don't block a real lead over our own infra hiccup.
        }
      }

      const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID as
        | string
        | undefined;
      const templateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID as
        | string
        | undefined;
      const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY as
        | string
        | undefined;

      if (!serviceId || !templateId || !publicKey) {
        const targetEmail =
          contact?.formTargetEmail || contact?.email || "contact@figtures.com";
        const subject =
          lang === "tr"
            ? `Yeni İş Birliği Talebi - ${trimmed.name}`
            : `New Collaboration Request - ${trimmed.name}`;
        const body =
          lang === "tr"
            ? `Ad: ${trimmed.name}\nE-posta: ${trimmed.email}${trimmed.projectType ? `\nProje türü: ${trimmed.projectType}` : ""}\n\nMesaj:\n${trimmed.message}${attributionFooter}`
            : `Name: ${trimmed.name}\nEmail: ${trimmed.email}${trimmed.projectType ? `\nProject type: ${trimmed.projectType}` : ""}\n\nMessage:\n${trimmed.message}${attributionFooter}`;
        const mailtoUrl = `mailto:${targetEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        window.location.href = mailtoUrl;
        toast({
          title:
            lang === "tr"
              ? "E-posta uygulaması açılıyor"
              : "Opening email client",
          description:
            lang === "tr"
              ? "EmailJS ayarı eksik olduğu için mesajınız varsayılan e-posta uygulamasıyla gönderilecek."
              : "EmailJS is not configured, so your default email client will be used.",
        });
        return;
      }

      const emailTitle =
        lang === "tr"
          ? `Yeni İş Birliği Talebi - ${trimmed.name}`
          : `New Collaboration Request - ${trimmed.name}`;
      const targetEmail =
        contact?.formTargetEmail || contact?.email || "contact@figtures.com";

      const leadId = generateLeadId();
      const emailParams = buildContactEmailJsParams({
        lang,
        trimmed,
        leadAttribution,
        hearAboutLabel,
        emailTitle,
        targetEmail,
        leadId,
      });

      await emailjs.send(serviceId, templateId, emailParams, { publicKey });

      setFormData({ name: "", email: "", message: "", projectType: "", hearAbout: "" });
      trackContactFormLead(lang, {
        ...leadAttribution,
        leadId,
        hearAbout: hearAboutLabel || undefined,
        projectType:
          trimmed.projectType || leadAttribution?.projectTypeRaw || undefined,
      });
      navigate("/tesekkurler");
    } catch {
      toast({
        title: lang === "tr" ? "Gönderim başarısız" : "Send failed",
        description:
          lang === "tr"
            ? "Mesaj gönderilemedi. Lütfen tekrar deneyin."
            : "Could not send your message. Please try again.",
      });
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const stats = [
    {
      value: STUDIO_PUBLIC_STATS.averageResponseHours,
      suffix: "h",
      label: t("contact.heroStat3"),
    },
  ];

  const stagger = {
    hidden: {},
    visible: {
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] as const },
    },
  };

  return (
    <section ref={ref} className="section-y relative" id="contact">
      {/* Background ambient glow */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.div
          className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full opacity-[0.03]"
          style={{
            background:
              "radial-gradient(circle, hsl(var(--foreground)) 0%, transparent 70%)",
          }}
          animate={{ scale: [1, 1.15, 1], rotate: [0, 90, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />
        <motion.div
          className="absolute -bottom-60 -left-40 w-[500px] h-[500px] rounded-full opacity-[0.02]"
          style={{
            background:
              "radial-gradient(circle, hsl(var(--foreground)) 0%, transparent 70%)",
          }}
          animate={{ scale: [1.1, 1, 1.1] }}
          transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
        />
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative">
        {/* Hero headline — full width, dramatic */}
        <motion.div
          variants={stagger}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="section-head-xl"
        >
          <motion.p
            variants={fadeUp}
            className="font-body text-xs tracking-[0.3em] text-muted-foreground mb-4 uppercase"
          >
            {t("contact.tag")}
          </motion.p>

          <Heading
            variants={fadeUp}
            className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-[1.02] tracking-tight max-w-4xl"
          >
            {contactTitle}
          </Heading>

          <motion.div variants={fadeUp} className="mt-6 max-w-3xl space-y-4">
            <div className="flex items-center gap-6">
              <div className="w-16 h-px bg-foreground/30 shrink-0" />
              <p className="font-body text-lg text-muted-foreground">
                {contactDescription}
              </p>
            </div>
            {leadAttribution?.toolId !== "project-cost-estimator" ? (
              <p className="font-body text-sm text-muted-foreground leading-relaxed pl-[4.5rem]">
                {t("contact.budgetToolHint")}{" "}
                <Link
                  to="/tools/project-cost-estimator"
                  className="text-foreground underline underline-offset-4 hover:text-muted-foreground transition-colors"
                >
                  {lang === "tr" ? "Maliyet tahmincisi →" : "Cost estimator →"}
                </Link>
              </p>
            ) : null}
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          className="mb-10 overflow-hidden rounded-3xl border border-border/60 bg-secondary/20"
        >
          <div className="grid grid-cols-1 divide-y divide-border/60 lg:grid-cols-3 lg:divide-x lg:divide-y-0">
            <div className="p-8 sm:p-10">
              <p className="font-body text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-3">
                {t("contact.infoLabel.email")}
              </p>
              <a
                href={`mailto:${contactEmail}`}
                className="font-display text-xl lg:text-2xl text-foreground hover:opacity-70 transition-opacity"
              >
                {contactEmail}
              </a>
            </div>
            <div className="p-8 sm:p-10">
              <p className="font-body text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-3">
                {t("contact.infoLabel.phone")}
              </p>
              <a
                href={`tel:${CONTACT_PHONE_E164}`}
                onClick={() => trackPhoneClick(lang, "contact_section")}
                className="font-display text-xl lg:text-2xl text-foreground hover:opacity-70 transition-opacity"
              >
                {CONTACT_PHONE_DISPLAY}
              </a>
            </div>
            <div className="p-8 sm:p-10">
              <p className="font-body text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-3">
                {t("contact.infoLabel.location")}
              </p>
              <p className="font-body text-sm text-foreground/80 leading-relaxed">
                {locationLabel}
              </p>
              <a
                href={BUSINESS_LOCATION.googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-3 font-body text-xs tracking-wide uppercase text-muted-foreground hover:text-foreground transition-colors"
              >
                {lang === "tr" ? "Haritada gör →" : "View on map →"}
              </a>
            </div>
          </div>
        </motion.div>

        <PremiumDivider className="mb-8" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-start">
          <motion.form
            id={CONTACT_FORM_ID}
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 50 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7 order-1 scroll-mt-24"
          >
            <PremiumSurface glow className="p-8 sm:p-10 lg:p-11 space-y-8">
              {leadAttribution?.blogSlug ? (
                <input
                  type="hidden"
                  name="blog_slug"
                  value={leadAttribution.blogSlug}
                  readOnly
                  aria-hidden
                  tabIndex={-1}
                />
              ) : null}
              {leadAttribution ? (
                <input
                  type="hidden"
                  name="lead_source"
                  value={leadAttribution.source}
                  readOnly
                  aria-hidden
                  tabIndex={-1}
                />
              ) : null}
              {/* Honeypot — invisible to sighted users and screen readers; bots that fill every input trip it. */}
              <input
                type="text"
                name="company_website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                autoComplete="off"
                tabIndex={-1}
                aria-hidden="true"
                className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden"
              />
              {!formEnabled && (
                <motion.p
                  initial={{ opacity: 0, y: 12 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5 }}
                  className="font-body text-sm text-muted-foreground"
                >
                  {lang === "tr"
                    ? "İletişim formu şu anda devre dışı. Bize e-posta ile ulaşabilirsiniz."
                    : "The contact form is currently disabled. You can reach us via email."}
                </motion.p>
              )}
              {toolLeadSummary ? (
                <p
                  className="font-body text-sm text-muted-foreground rounded-2xl border border-border/50 bg-secondary/30 px-4 py-3"
                  role="status"
                  data-testid="contact-tool-lead-summary"
                >
                  {toolLeadSummary}
                </p>
              ) : null}
              <motion.div
                className="relative group mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{
                  delay: 0.38,
                  duration: 0.6,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <PremiumFieldLabel htmlFor={CONTACT_FORM_FIRST_FIELD_ID}>
                  {t("contact.projectType")}
                </PremiumFieldLabel>
                <select
                  id={CONTACT_FORM_FIRST_FIELD_ID}
                  value={formData.projectType}
                  onChange={(e) =>
                    setFormData({ ...formData, projectType: e.target.value })
                  }
                  className="w-full bg-transparent border-b border-border/60 py-2 font-body text-foreground text-lg focus:outline-none transition-colors duration-500"
                >
                  <option value="">
                    {t("contact.projectTypePlaceholder")}
                  </option>
                  <option value={t("contact.projectType.ai")}>
                    {t("contact.projectType.ai")}
                  </option>
                  <option value={t("contact.projectType.saas")}>
                    {t("contact.projectType.saas")}
                  </option>
                  <option value={t("contact.projectType.mobile")}>
                    {t("contact.projectType.mobile")}
                  </option>
                  <option value={t("contact.projectType.custom")}>
                    {t("contact.projectType.custom")}
                  </option>
                  <option value={t("contact.projectType.other")}>
                    {t("contact.projectType.other")}
                  </option>
                </select>
              </motion.div>
              <motion.div
                className="relative group mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{
                  delay: 0.42,
                  duration: 0.6,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <PremiumFieldLabel htmlFor="contact-hearAbout">
                  {t("contact.hearAbout")}
                </PremiumFieldLabel>
                <select
                  id="contact-hearAbout"
                  value={formData.hearAbout}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      hearAbout: e.target.value as HearAboutOptionId | "",
                    })
                  }
                  className="w-full bg-transparent border-b border-border/60 py-2 font-body text-foreground text-lg focus:outline-none transition-colors duration-500"
                >
                  <option value="">{t("contact.hearAboutPlaceholder")}</option>
                  {HEAR_ABOUT_OPTION_IDS.map((id) => (
                    <option key={id} value={id}>
                      {t(`contact.hearAbout.${id}`)}
                    </option>
                  ))}
                </select>
              </motion.div>
              {(["name", "email", "message"] as const).map((field, i) => (
                <motion.div
                  key={field}
                  className="relative group mb-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{
                    delay: 0.4 + i * 0.1,
                    duration: 0.6,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                >
                  <PremiumFieldLabel
                    htmlFor={`contact-${field}`}
                    active={focused === field}
                  >
                    {t(`contact.${field}`)}
                  </PremiumFieldLabel>
                  {field === "message" ? (
                    <textarea
                      id={`contact-${field}`}
                      rows={5}
                      value={formData[field]}
                      onChange={(e) =>
                        setFormData({ ...formData, [field]: e.target.value })
                      }
                      onFocus={() => setFocused(field)}
                      onBlur={() => setFocused(null)}
                      className="w-full bg-transparent border-b border-border/60 py-2 font-body text-foreground text-lg focus:outline-none transition-colors duration-500 resize-none placeholder:text-muted-foreground"
                      placeholder={t("contact.messagePlaceholder")}
                    />
                  ) : (
                    <input
                      id={`contact-${field}`}
                      type={field === "email" ? "email" : "text"}
                      value={formData[field]}
                      onChange={(e) =>
                        setFormData({ ...formData, [field]: e.target.value })
                      }
                      onFocus={() => setFocused(field)}
                      onBlur={() => setFocused(null)}
                      className="w-full bg-transparent border-b border-border/60 py-2 font-body text-foreground text-lg focus:outline-none transition-colors duration-500 placeholder:text-muted-foreground"
                    />
                  )}
                  {/* Active line */}
                  <motion.div
                    className="absolute bottom-0 left-0 h-[2px] bg-foreground origin-left"
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: focused === field ? 1 : 0 }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  />
                  {/* Glow on focus */}
                  <motion.div
                    className="absolute bottom-0 left-0 right-0 h-8 pointer-events-none"
                    style={{
                      background:
                        "linear-gradient(to top, hsl(var(--foreground) / 0.03), transparent)",
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: focused === field ? 1 : 0 }}
                    transition={{ duration: 0.4 }}
                  />
                </motion.div>
              ))}

              {turnstileSiteKey && (
                <div ref={turnstileContainerRef} className="pt-2" />
              )}

              <motion.div
                className="pt-4"
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.7, duration: 0.6 }}
              >
                <motion.button
                  type="submit"
                  disabled={isSubmitting || !formEnabled}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.98 }}
                  className="group relative w-full sm:w-auto font-body text-sm px-12 py-4 bg-foreground text-background rounded-full font-medium overflow-hidden disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {/* Shimmer effect */}
                  <motion.div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{
                      background:
                        "linear-gradient(105deg, transparent 40%, hsl(var(--background) / 0.15) 50%, transparent 60%)",
                    }}
                    animate={{ x: ["-100%", "200%"] }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatDelay: 1,
                    }}
                  />
                  <span className="relative z-10">
                    {isSubmitting
                      ? lang === "tr"
                        ? "Gönderiliyor..."
                        : "Sending..."
                      : t("contact.send")}
                  </span>
                </motion.button>
              </motion.div>
            </PremiumSurface>
          </motion.form>

          <motion.aside
            className="lg:col-span-5 flex flex-col gap-6 lg:sticky lg:top-28 order-2"
            initial={{ opacity: 0, y: 50 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{
              duration: 0.9,
              delay: 0.45,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            {stats.map((stat) => (
              <PremiumSurface key={stat.label} className="p-8">
                <div className="flex items-end justify-between gap-6">
                  <div>
                    <p className="font-body text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-2">
                      {stat.label}
                    </p>
                    <p className="font-body text-sm text-muted-foreground max-w-[14rem] leading-relaxed">
                      {t("contact.heroStat3Desc")}
                    </p>
                  </div>
                  <AnimatedCounter target={stat.value} suffix={stat.suffix} />
                </div>
              </PremiumSurface>
            ))}

            <PremiumSurface className="overflow-hidden p-0">
              <div className="aspect-[4/3] w-full">
                <iframe
                  title={
                    lang === "tr"
                      ? "Figtures ofis konumu"
                      : "Figtures office location"
                  }
                  src={mapEmbedUrl}
                  className="h-full w-full min-h-[240px] border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </PremiumSurface>

            {socialLinks.length > 0 && (
              <PremiumSurface className="p-8">
                <p className="font-body text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-4">
                  {t("contact.infoLabel.social")}
                </p>
                <div className="flex flex-wrap gap-3">
                  {socialLinks.map((social) => (
                    <a
                      key={`${social.platform}-${social.url}`}
                      href={social.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="rounded-full border border-border/60 px-4 py-2 font-body text-xs text-foreground/70 hover:border-foreground/30 hover:text-foreground transition-colors"
                    >
                      {social.platform}
                    </a>
                  ))}
                </div>
              </PremiumSurface>
            )}
          </motion.aside>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
